import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GalleryAsset } from '@xphoto/shared';
import {
  ApiError,
  fetchAssetPage,
  fetchAssetPosition,
  thumbnailUrl,
  type GalleryFilters,
} from '../gallery/api';
import { originalUrl } from './viewer-api';
import {
  NEIGHBOUR_FETCH_RADIUS,
  hasNext,
  hasPrev,
  preloadIndices,
  windowRange,
  type Direction,
} from './neighbours';

/**
 * Navigation de la visionneuse sur l'ENSEMBLE ORDONNÉ COMPLET (mêmes tri/filtres
 * que la galerie), FIABLE pour TOUTE image — à chaud (reliée à la grille) comme à
 * froid (`/photo/:id` copié-collé ou hors fenêtre chargée).
 *
 * Corrige le bug « impossible d'aller en avant/arrière sur certaines images » :
 * avant, préc./suiv. ne marchaient que si les voisins étaient déjà dans la fenêtre
 * virtualisée de la grille. Ici, on résout l'INDEX ABSOLU de l'asset (via la grille
 * si connue, sinon via l'endpoint de position) puis on charge une FENÊTRE de voisins
 * autour de lui → ids préc./suiv. TOUJOURS corrects (`canPrev = index>0`,
 * `canNext = index<total-1`).
 *
 * Préchargement directionnel (bug B) : on mémorise le SENS du dernier saut et on
 * précharge les ORIGINAUX voisins dans ce sens (plusieurs en avant, 1 en arrière) —
 * navigation quasi instantanée. Les originaux étant EXEMPTÉS du rate-limit
 * (allow-list v0.16.1), aucun risque de 429.
 *
 * DÉGRADATION PROPRE : si la position ou la fenêtre échoue (réseau, 404…), on garde
 * l'asset courant affichable (préc./suiv. simplement indisponibles) plutôt que de
 * planter — le hook n'est jamais bloquant pour l'affichage.
 */

/** Précharge un original (injectable pour les tests). Défaut : `new Image()`. */
export type Preloader = (url: string) => void;

const defaultPreloader: Preloader = (url) => {
  // `new Image()` déclenche le téléchargement dans le cache HTTP du navigateur, sans
  // insérer dans le DOM ni bloquer le thread. `fetchPriority=low` : cède la priorité
  // à l'original activement regardé (données « high », cf. v0.16.6).
  try {
    const img = new Image();
    img.decoding = 'async';
    if ('fetchPriority' in img) {
      (img as HTMLImageElement & { fetchPriority: string }).fetchPriority = 'low';
    }
    img.src = url;
  } catch {
    // Environnement sans `Image` (test/SSR) : no-op silencieux.
  }
};

export interface ViewerNavigation {
  /** Index absolu courant dans l'ensemble ordonné, ou `null` si non (encore) résolu. */
  index: number | null;
  /** Total de l'ensemble filtré/trié (0 tant qu'inconnu). */
  total: number;
  /** Préc. disponible (index absolu > 0). */
  canPrev: boolean;
  /** Suiv. disponible (index absolu < total-1). */
  canNext: boolean;
  /** Id du voisin précédent (chargé), ou `null` si indisponible. */
  prevId: string | null;
  /** Id du voisin suivant (chargé), ou `null` si indisponible. */
  nextId: string | null;
  /** À appeler juste avant de naviguer vers le voisin précédent (met à jour le sens/index). */
  notifyPrev: () => void;
  /** À appeler juste avant de naviguer vers le voisin suivant (met à jour le sens/index). */
  notifyNext: () => void;
}

interface UseViewerNavigationArgs {
  libraryId: string;
  assetId: string | null;
  filters: GalleryFilters;
  /** Index déjà connu par la grille (ouverture à chaud), sinon `null` → endpoint position. */
  knownIndex: number | null;
  /** Total déjà connu par la grille (ouverture à chaud), sinon `null`. */
  knownTotal: number | null;
  /**
   * Accès aux assets déjà chargés par la GRILLE (index absolu → asset). Permet de
   * lire les voisins sans refetch quand la grille les a déjà (ouverture à chaud).
   * `undefined` → aucun accès grille (accès à froid).
   */
  gridAsset?: (index: number) => GalleryAsset | undefined;
  /** Injection du préchargeur (tests). */
  preloader?: Preloader;
}

/** Sérialise les filtres pour une dépendance stable (évite les resets inutiles). */
function filtersKeyOf(filters: GalleryFilters): string {
  return JSON.stringify(filters);
}

export function useViewerNavigation({
  libraryId,
  assetId,
  filters,
  knownIndex,
  knownTotal,
  gridAsset,
  preloader = defaultPreloader,
}: UseViewerNavigationArgs): ViewerNavigation {
  // Map index absolu → asset (voisins chargés), local à la visionneuse.
  const byIndexRef = useRef<Map<number, GalleryAsset>>(new Map());
  const [index, setIndex] = useState<number | null>(knownIndex);
  const [total, setTotal] = useState<number>(knownTotal ?? 0);
  const [, setVersion] = useState(0);

  const directionRef = useRef<Direction>('forward');
  /**
   * Id du voisin vers lequel NOUS venons de sauter (posé par `notifyPrev`/`notifyNext`,
   * qui ont DÉJÀ décalé l'index localement). Sert à ne PAS redemander le rang absolu au
   * serveur pour un saut dont on connaît déjà la réponse — cf. l'effet de résolution.
   */
  const selfNavigatedIdRef = useRef<string | null>(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const filtersKey = useMemo(() => filtersKeyOf(filters), [filters]);

  const preloaderRef = useRef(preloader);
  preloaderRef.current = preloader;
  const gridAssetRef = useRef(gridAsset);
  gridAssetRef.current = gridAsset;

  /** Voisin à l'index absolu : grille d'abord (à chaud), sinon map locale (fenêtre). */
  const resolveNeighbour = useCallback((i: number): GalleryAsset | undefined => {
    return gridAssetRef.current?.(i) ?? byIndexRef.current.get(i);
  }, []);

  // Reset complet à chaque changement de requête (filtres/lib) : la map de voisins
  // n'est plus valable (l'ensemble ordonné a changé).
  useEffect(() => {
    byIndexRef.current = new Map();
    // L'ensemble ordonné a changé : un saut « à nous » d'avant le reset ne prouve plus
    // rien sur le rang courant → on redemandera la position au serveur.
    selfNavigatedIdRef.current = null;
    setIndex(knownIndex);
    setTotal(knownTotal ?? 0);
    setVersion((v) => v + 1);
    // Dépendances volontaires : on RESET seulement quand la requête change
    // (`libraryId`/`filtersKey`) — sinon la map de voisins serait vidée à chaque saut.
    // `knownIndex`/`knownTotal` sont lus au reset et re-synchronisés par l'effet dédié
    // ci-dessous (pas de règle exhaustive-deps active sur ce repo, cf. `use-assets`).
  }, [libraryId, filtersKey]);

  // Synchronise l'index/total connus de la grille (ouverture à chaud) : si la grille
  // fournit un index pour l'asset courant, il fait autorité (évite un appel réseau).
  useEffect(() => {
    if (knownIndex !== null) setIndex(knownIndex);
    if (knownTotal !== null) setTotal((prev) => (knownTotal > 0 ? knownTotal : prev));
  }, [knownIndex, knownTotal]);

  // Résout l'index absolu à FROID (grille muette) via l'endpoint de position.
  //
  // ⚠ UN SEUL APPEL PAR ENTRÉE À FROID, pas un par flèche (audit — montée en charge).
  // Avant, l'effet ne se gardait que sur `knownIndex !== null` : hors de la fenêtre
  // chargée de la grille (deep-link `/photo/:id`, ou simple feuilletage au-delà du
  // préfixe chargé), `knownIndex` reste `null` À CHAQUE PAS, donc chaque flèche
  // redemandait le rang absolu au serveur. Or `position()` est la requête la plus lourde
  // de l'API : elle compte les lignes qui trient AVANT l'asset, ce qui interdit toute
  // fenêtre — et en tri ALÉATOIRE l'ordre étant `md5(graine || id)`, aucun index n'est
  // possible par construction : c'est un balayage complet avec un md5 PAR LIGNE.
  // Mesuré sur 50 000 assets : ~48 ms pour le rang + ~11 ms pour le total, à chaque
  // pression de flèche.
  //
  // Or la réponse est CONNUE : `notifyPrev`/`notifyNext` ont déjà décalé l'index de ±1,
  // et l'ensemble ordonné n'a pas bougé (tout changement de filtre passe par le reset
  // ci-dessus, qui efface `selfNavigatedIdRef`). On saute donc l'appel quand le nouvel
  // `assetId` est EXACTEMENT le voisin vers lequel nous venons nous-mêmes de sauter.
  //
  // Le compteur « n / total » est ainsi INTÉGRALEMENT préservé, dans tous les tris —
  // là où court-circuiter `position()` en aléatoire aurait obligé à le masquer.
  useEffect(() => {
    if (!libraryId || assetId === null) return;
    if (knownIndex !== null) return; // déjà connu par la grille → pas d'appel.
    if (selfNavigatedIdRef.current === assetId) return; // saut local : index déjà à jour.
    const controller = new AbortController();
    fetchAssetPosition(libraryId, assetId, filtersRef.current, controller.signal)
      .then((pos) => {
        if (controller.signal.aborted) return;
        setIndex(pos.index);
        setTotal(pos.total);
        // Amorce la map avec l'asset courant à son index (si déjà connu ailleurs, la
        // fenêtre le confirmera).
        setVersion((v) => v + 1);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        // Dégradation propre : sans position, préc./suiv. restent indisponibles.
        if (!(err instanceof ApiError)) return;
      });
    return () => controller.abort();
  }, [libraryId, assetId, knownIndex]);

  // Précharge directionnellement les ORIGINAUX voisins résolvables (grille ou map).
  //
  // ⚠ L'URL préchargée doit être IDENTIQUE, caractère pour caractère, à celle que la
  // visionneuse demandera : le cache HTTP du navigateur est indexé par URL, pas par
  // ressource. Le `rev` (préfixe du `file_hash`, cache-buster des écritures EXIF — cf.
  // `originalUrl`) en fait partie, il DOIT donc être apposé ici aussi.
  //
  // Sans lui, le préchargement était INERTE depuis la v1.31.0 : ce code a été écrit en
  // v0.23.0, quand `originalUrl()` ne prenait qu'un id ; le `rev` lui a été ajouté côté
  // AFFICHAGE seulement. Or l'API galerie peuple TOUJOURS `rev` (`substr(file_hash,1,8)`),
  // donc les deux URLs divergeaient systématiquement : l'octet préchargé atterrissait dans
  // une entrée de cache orpheline (`…/original`) que personne ne redemandait jamais,
  // pendant que l'affichage allait chercher `…/original?rev=…`. Chaque photo était donc
  // re-téléchargée au moment de l'afficher, et le préchargement DOUBLAIT le trafic au lieu
  // de l'économiser. Garde : `use-viewer-navigation.test.tsx` (jumelle dans `Viewer.test.tsx`).
  const preloadAround = useCallback(
    (fromIndex: number, tot: number) => {
      const targets = preloadIndices(fromIndex, tot, directionRef.current);
      for (const ti of targets) {
        const neighbour = resolveNeighbour(ti);
        if (!neighbour) continue;
        const url =
          neighbour.kind === 'image'
            ? originalUrl(neighbour.id, neighbour.rev)
            : // Vidéo : pas d'original affichable en <img> → on précharge son poster 1024.
              thumbnailUrl(neighbour.id, 1024, neighbour.rev);
        preloaderRef.current(url);
      }
    },
    [resolveNeighbour],
  );

  // Charge la FENÊTRE de voisins autour de l'index courant + précharge directionnel.
  useEffect(() => {
    if (!libraryId || index === null || total <= 0) return;

    // Si la fenêtre de voisins est DÉJÀ COUVERTE, pas de refetch : on lit les voisins en
    // mémoire et on se contente de précharger.
    //
    // ⚠ On consulte les DEUX sources (`resolveNeighbour` = grille OU map locale), comme
    // le fait le résolveur de `prevId`/`nextId`. Avant, ce test ne regardait QUE la
    // grille : en accès à froid ou en mode série elle est muette, donc la couverture
    // était fausse D'EMBLÉE et l'effet re-téléchargeait la fenêtre à CHAQUE pas. Avec un
    // rayon de 5, la fenêtre glisse de 1 : 10 des 11 éléments étaient déjà en mémoire —
    // ~11× trop de requêtes en feuilletage rapide.
    const { offset, limit } = windowRange(index, total);
    if (limit <= 0) return;
    let covered = true;
    for (let i = offset; covered && i < offset + limit; i += 1) {
      if (resolveNeighbour(i) === undefined) covered = false;
    }
    if (covered) {
      preloadAround(index, total);
      return;
    }

    // Sinon on charge un bloc PLUS LARGE que le rayon exigé (cf. `NEIGHBOUR_FETCH_RADIUS`) :
    // la couverture tient alors une dizaine de pas et l'effet se tait, au lieu de refaire
    // une requête à chaque saut pour n'y gagner qu'un seul élément de bord.
    const fetchWindow = windowRange(index, total, NEIGHBOUR_FETCH_RADIUS);
    const controller = new AbortController();
    fetchAssetPage(
      libraryId,
      filtersRef.current,
      fetchWindow.offset,
      fetchWindow.limit,
      controller.signal,
    )
      .then((page) => {
        if (controller.signal.aborted) return;
        const map = byIndexRef.current;
        for (let i = 0; i < page.items.length; i += 1) {
          const item = page.items[i];
          if (item) map.set(fetchWindow.offset + i, item);
        }
        if (page.total !== total) setTotal(page.total);
        setVersion((v) => v + 1);
        preloadAround(index, total);
      })
      .catch(() => {
        // Dégradation propre : la fenêtre a échoué (réseau/429 sur la liste) → on garde
        // l'asset courant ; préc./suiv. peuvent rester indisponibles ce tour-ci.
      });
    return () => controller.abort();
  }, [libraryId, index, total, filtersKey, preloadAround, resolveNeighbour]);

  const canPrev = index !== null && hasPrev(index);
  const canNext = index !== null && hasNext(index, total);

  const prevId = index !== null ? (resolveNeighbour(index - 1)?.id ?? null) : null;
  const nextId = index !== null ? (resolveNeighbour(index + 1)?.id ?? null) : null;

  // Voisins du rendu courant, lisibles depuis les callbacks stables (`useCallback([])`)
  // sans les faire dépendre de `prevId`/`nextId` — l'appelant garde des références
  // stables d'un rendu à l'autre.
  const neighbourIdsRef = useRef<{ prev: string | null; next: string | null }>({
    prev: null,
    next: null,
  });
  neighbourIdsRef.current = { prev: prevId, next: nextId };

  const notifyPrev = useCallback(() => {
    directionRef.current = 'backward';
    // On sait vers QUI on saute : l'effet de résolution n'aura pas à redemander le rang
    // absolu au serveur pour cet asset (l'index est décalé localement, juste en dessous).
    selfNavigatedIdRef.current = neighbourIdsRef.current.prev;
    setIndex((i) => (i === null ? i : Math.max(0, i - 1)));
  }, []);
  const notifyNext = useCallback(() => {
    directionRef.current = 'forward';
    selfNavigatedIdRef.current = neighbourIdsRef.current.next;
    setIndex((i) => (i === null ? i : i + 1));
  }, []);

  return { index, total, canPrev, canNext, prevId, nextId, notifyPrev, notifyNext };
}
